import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useSession } from '../session';

/**
 * The console's door.
 *
 * POST /api/users/login answers an identical `401 Invalid credentials` whether
 * the password is wrong or no such account exists, and that is deliberate — it
 * is what stops anyone using this form to find out who the customers are.
 *
 * So this screen does NOT try to detect "you have no company yet". It OFFERS the
 * registration route instead: a permanent, visible action, repeated in the error
 * state as a suggestion. Same destination, same number of clicks, and the
 * defence survives. (WEB-APP-PLAN §2.1)
 */
export default function Login() {
  const { signIn } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signIn(email.trim(), password);
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(0, 'Something went wrong. Try again.'));
    } finally {
      setBusy(false);
    }
  }

  // An owner who exists but has not been granted the console gets a different
  // answer from a bad password, because they have already proved who they are.
  const denied = error?.isWebAccessDenied;

  return (
    <div className="login">
      <form className="login__card card" onSubmit={onSubmit}>
        <div className="login__brand">
          <span className="login__mark" aria-hidden="true" />
          <span className="login__name">BhoomiTrack</span>
        </div>
        <h1 className="login__title">Sign in to your console</h1>
        <p className="muted login__sub">For owners. Managers work in the Android app.</p>

        <label className="label" htmlFor="email">Email</label>
        <input
          id="email" className="field" type="email" autoComplete="username"
          value={email} onChange={(e) => setEmail(e.target.value)} required
          placeholder="you@company.com"
        />

        <label className="label" htmlFor="password" style={{ marginTop: 'var(--s-base)' }}>Password</label>
        <input
          id="password" className="field" type="password" autoComplete="current-password"
          value={password} onChange={(e) => setPassword(e.target.value)} required
        />

        {error && (
          <div className={`login__error ${denied ? 'login__error--info' : ''}`} role="alert">
            <strong>{error.message}</strong>
            {denied ? (
              <span className="muted">
                Your account works in the Android app. The web console is granted by your Super Admin.
              </span>
            ) : (
              <span className="muted">
                No company on BhoomiTrack yet? <Link className="login__link" to="/register">Register your company</Link>.
              </span>
            )}
          </div>
        )}

        <button className="btn btn--gold login__submit" type="submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>

        {/* Permanent, not conditional. The API cannot tell us they need it. */}
        <p className="login__foot muted">
          New here? <Link className="login__link" to="/register">Register your company</Link>
          <span aria-hidden="true"> · </span>
          <a className="login__link" href="https://bhoomitrack.com">bhoomitrack.com</a>
        </p>
      </form>
    </div>
  );
}
